/**
 * Единый сокет чатов v2 — GET /v2/chats/ws.
 *
 * Один на пользователя: в нём события всех чатов, где он участник. Канал
 * только на чтение — отправка, правка и «прочитано» остаются REST-запросами.
 * Открывается при входе в приложение (AppBootstraps) и держится, пока жив хоть
 * один подписчик: счётчик запусков, а не сокет на каждый экран.
 *
 * История событий на сервере не хранится. После обрыва (сеть, деплой, сервер
 * отключил отстающего) переподключаемся с нарастающей паузой и сообщаем
 * подписчикам через `subscribeChatReconnect` — они дочитывают пропущенное
 * обычными GET /v2/chats и …/messages.
 *
 * Токен уходит в query: заголовок браузерному WebSocket не выставить. Access
 * живёт 15 минут, поэтому перед подключением проверяем его срок и при
 * необходимости обновляем пару — иначе рукопожатие падало бы с 401 по кругу.
 */
import { getAccessToken, refreshSession } from '@/shared/api';

import { chatsSocketUrl, normalizeMessage } from '../api/chats';
import type { ChatEvent, ChatMessage } from '../model/types';

type EventListener = (event: ChatEvent) => void;
type ReconnectListener = () => void;

const eventListeners = new Set<EventListener>();
const reconnectListeners = new Set<ReconnectListener>();

let starts = 0;
let socket: WebSocket | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let attempts = 0;
/** Было ли уже хоть одно успешное подключение: следующие — это переподключения. */
let everOpened = false;

const MAX_DELAY_MS = 30_000;
/** Обновляем токен, если до конца его жизни меньше этого. */
const TOKEN_MARGIN_MS = 30_000;

/** Срок жизни JWT из его payload; подпись здесь не проверяем — это делает бэк. */
function tokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;

    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

async function freshToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt !== null && expiresAt - Date.now() < TOKEN_MARGIN_MS) {
    return refreshSession();
  }

  return token;
}

const TYPES = new Set([
  'message.created',
  'message.updated',
  'message.deleted',
  'chat.read',
  'reaction.toggled',
  'pin.changed',
]);

function parse(raw: unknown): ChatEvent | null {
  let frame: unknown;
  try {
    frame = JSON.parse(String(raw));
  } catch {
    return null;
  }
  if (!frame || typeof frame !== 'object') {
    return null;
  }
  const event = frame as Record<string, unknown>;
  if (
    typeof event.type !== 'string' ||
    !TYPES.has(event.type) ||
    typeof event.chatId !== 'string'
  ) {
    return null;
  }
  if (event.message && typeof event.message === 'object') {
    event.message = normalizeMessage(event.message as ChatMessage);
  }

  return event as unknown as ChatEvent;
}

function schedule() {
  if (starts === 0 || timer) {
    return;
  }
  attempts += 1;
  const delay = Math.min(MAX_DELAY_MS, 1000 * 2 ** (attempts - 1));
  timer = setTimeout(() => {
    timer = null;
    void connect();
  }, delay);
}

async function connect() {
  if (starts === 0 || socket) {
    return;
  }
  const token = await freshToken();
  if (starts === 0 || socket) {
    return;
  }
  if (!token) {
    // Нативной сессии нет (или обновить не вышло) — пробуем позже.
    schedule();
    return;
  }

  let ws: WebSocket;
  try {
    ws = new WebSocket(chatsSocketUrl(token));
  } catch {
    schedule();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    attempts = 0;
    if (everOpened) {
      for (const listener of [...reconnectListeners]) {
        listener();
      }
    }
    everOpened = true;
  };
  ws.onmessage = (message) => {
    const event = parse(message.data);
    if (!event) {
      return;
    }
    for (const listener of [...eventListeners]) {
      listener(event);
    }
  };
  ws.onclose = () => {
    if (socket === ws) {
      socket = null;
    }
    schedule();
  };
}

/**
 * Запустить сокет. Возвращает функцию остановки; сам сокет закрывается,
 * когда остановлены все запуски.
 */
export function startChatSocket(): () => void {
  starts += 1;
  void connect();

  let stopped = false;
  return () => {
    if (stopped) {
      return;
    }
    stopped = true;
    starts -= 1;
    if (starts > 0) {
      return;
    }
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const current = socket;
    socket = null;
    attempts = 0;
    everOpened = false;
    if (current && current.readyState <= WebSocket.OPEN) {
      current.close();
    }
  };
}

/** События всех моих чатов. Фильтровать по chatId — забота подписчика. */
export function subscribeChatEvents(listener: EventListener): () => void {
  eventListeners.add(listener);

  return () => {
    eventListeners.delete(listener);
  };
}

/** Сокет переподключился: пора дочитать пропущенное обычными GET. */
export function subscribeChatReconnect(
  listener: ReconnectListener
): () => void {
  reconnectListeners.add(listener);

  return () => {
    reconnectListeners.delete(listener);
  };
}
